import { useState } from 'react';
import {
  Button,
  Icon,
  useDisclosure,
  ModalBody,
  ModalFooter,
  Box,
  Text,
  VStack,
  HStack,
  Badge,
  Textarea,
  Select,
  Input,
} from '@chakra-ui/react';
import { RiEditLine } from 'react-icons/ri';

import { ModalComponent } from '..';
import {
  IExpenseReport,
  AccountingStatus,
} from '../../../../pages/Dashboard/vehicles_management/Expenses';
import { expenseReportsService } from '../../../../services/expenseReportsService';
import { apllyToast } from '../../../Global/Toast2.0';
import { useUsers } from '../../../../hooks/useUsers';

interface IModalProps {
  report: IExpenseReport;
  onStatusUpdated: () => void;
}

export function ModalUpdateExpenseStatus({
  report,
  onStatusUpdated,
}: IModalProps) {
  const { onOpen, isOpen, onClose } = useDisclosure();
  const { getUserName } = useUsers();
  const [status, setStatus] = useState<AccountingStatus>(
    report.status || 'EM PROCESSO',
  );
  const [observation, setObservation] = useState(report.observation || '');
  const [dueDate, setDueDate] = useState(report.due_date || '');
  const [saving, setSaving] = useState(false);

  const handleOpen = () => {
    setStatus(report.status || 'EM PROCESSO');
    setObservation(report.observation || '');
    setDueDate(report.due_date || '');
    onOpen();
  };

  const handleSave = async () => {
    if (status === 'RECUSADO' && !observation.trim()) {
      apllyToast('warning', 'Informe o motivo da recusa');
      return;
    }

    if (status === 'RECUSADO' && observation.trim().length < 10) {
      apllyToast(
        'warning',
        'Descreva o motivo da recusa com pelo menos 10 caracteres',
      );
      return;
    }

    setSaving(true);
    try {
      await expenseReportsService.updateStatus(report.id, {
        status,
        observation: status === 'RECUSADO' ? observation : undefined,
        due_date: dueDate || undefined,
      });

      let successMsg = 'Status atualizado!';
      if (status === 'LANCADO') successMsg = 'Relatório marcado como lançado!';
      if (status === 'RECUSADO') successMsg = 'Relatório recusado.';
      apllyToast('success', successMsg);

      onStatusUpdated();
      onClose();
    } catch (err: any) {
      apllyToast(
        'error',
        err.response?.data?.message || 'Erro ao atualizar status',
      );
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (s: string) => {
    if (s === 'LANCADO') return 'green';
    if (s === 'RECUSADO') return 'red';
    if (s === 'AGUARDANDO LANCAMENTO') return 'yellow';
    return 'gray';
  };

  return (
    <Box display="inline-block">
      <Button
        size="sm"
        colorScheme="cyan"
        variant="ghost"
        onClick={handleOpen}
        title="Alterar Status do Lançamento"
      >
        <Icon as={RiEditLine} fontSize="20" />
      </Button>

      <ModalComponent
        title="Alterar Status do Lançamento"
        isOpen={isOpen}
        onClose={onClose}
        sizeSpecifyc="lg"
      >
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Resumo do relatório */}
            <Box bg="whiteAlpha.100" p={4} borderRadius="md">
              <HStack spacing={6} mb={2}>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Usuário
                  </Text>
                  <Text fontSize="sm" color="gray.300">
                    {report.director_name || getUserName(report.director)}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Status Atual
                  </Text>
                  <Badge
                    colorScheme={getStatusColor(report.status || 'EM PROCESSO')}
                    fontSize="xs"
                  >
                    {report.status || 'EM PROCESSO'}
                  </Badge>
                </Box>
              </HStack>
            </Box>

            {/* Seletor de novo status */}
            <Box>
              <Text fontSize="sm" fontWeight="600" color="gray.300" mb={2}>
                Novo Status
              </Text>
              <Select
                value={status}
                onChange={e => {
                  setStatus(e.target.value as AccountingStatus);
                }}
                bg="whiteAlpha.200"
                borderColor="whiteAlpha.300"
                color="gray.100"
              >
                <option
                  value="EM PROCESSO"
                  style={{ background: '#4B4D63', color: '#E2E8F0' }}
                >
                  EM PROCESSO
                </option>
                <option
                  value="AGUARDANDO LANCAMENTO"
                  style={{ background: '#4B4D63', color: '#E2E8F0' }}
                >
                  AGUARDANDO LANÇAMENTO
                </option>
                <option
                  value="LANCADO"
                  style={{ background: '#4B4D63', color: '#E2E8F0' }}
                >
                  LANÇADO
                </option>
                <option
                  value="RECUSADO"
                  style={{ background: '#4B4D63', color: '#E2E8F0' }}
                >
                  RECUSADO
                </option>
              </Select>
            </Box>

            {/* Vencimento manual — a contabilidade define a data para
                qualquer tipo de relatório (Ajuda de custo ou Ressarcimento) */}
            <Box>
              <Text fontSize="sm" fontWeight="600" color="gray.300" mb={2}>
                Vencimento
              </Text>
              <Input
                type="date"
                value={dueDate}
                onChange={e => setDueDate(e.target.value)}
                bg="whiteAlpha.200"
                borderColor="whiteAlpha.300"
                color="gray.100"
              />
            </Box>

            {/* Campo de observação obrigatório quando RECUSADO */}
            {status === 'RECUSADO' && (
              <Box>
                <Text fontSize="sm" fontWeight="600" color="red.300" mb={2}>
                  Motivo da Recusa *
                </Text>
                <Textarea
                  value={observation}
                  onChange={e => setObservation(e.target.value)}
                  placeholder="Descreva detalhadamente o motivo da recusa..."
                  rows={4}
                  bg="whiteAlpha.100"
                  borderColor="red.400"
                  color="gray.200"
                  _placeholder={{ color: 'gray.500' }}
                  _focus={{ borderColor: 'red.300' }}
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Mínimo de 10 caracteres
                </Text>
              </Box>
            )}

            {/* Preview do novo status */}
            <Box
              bg="whiteAlpha.100"
              p={3}
              borderRadius="md"
              borderLeft="3px solid"
              borderLeftColor={`${getStatusColor(status)}.400`}
            >
              <HStack>
                <Text fontSize="xs" color="gray.500">
                  O status será alterado para:
                </Text>
                <Badge colorScheme={getStatusColor(status)} fontSize="xs">
                  {status}
                </Badge>
              </HStack>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={saving}>
            Cancelar
          </Button>
          <Button
            colorScheme={getStatusColor(status)}
            onClick={handleSave}
            isLoading={saving}
            loadingText="Salvando..."
          >
            Confirmar
          </Button>
        </ModalFooter>
      </ModalComponent>
    </Box>
  );
}
